(ns dashboard.views.reports
  (:require
   [clojure.string :refer [split lower-case]]
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]
   [breaking-point.core :as bp]
   [headlessui-reagent.core :as ui]
   [dashboard.subs :as subs]
   [dashboard.utils :refer [listen format-percent]]
   [dashboard.components.button :refer [button-group]]
   [dashboard.components.stats :refer [stat-item]]
   [dashboard.components.table :refer [table pagination]]
   [dashboard.components.datepicker :refer [datepicker]]
   ["@heroicons/react/outline" :refer [CloudUploadIcon DocumentDownloadIcon DocumentSearchIcon ArchiveIcon]]
   ["date-fns" :refer [format subDays]]
   ["@nivo/bar" :refer [ResponsiveBar]]
   ["@nivo/bullet" :refer [ResponsiveBullet]]))

(defn- empty-report []
  [:div.border-4.border-dashed.border-gray-200.rounded-lg.h-96])

(defn- report-example []
  (let [bar-vis (reagent/atom :default)]
    (fn []
      [:div
       [:div.flex.justify-between
        [:h3.text-lg.leading-6.font-medium.text-gray-900
         "Last 30 days"]
        [:div
         [datepicker
          {:selected-date (js/Date.)}]]]

       ;; Stat Items
       [:dl.mt-5.grid.grid-cols-1.rounded-lg.bg-white.overflow-hidden.border.border-gray-200.divide-y.divide-gray-200.md:grid-cols-3.md:divide-y-0.md:divide-x
        (let [stat-items [{:name "Uploaded Documents"
                           :value (listen ::subs/items-count)
                           :change 0.23}
                          {:name "Completed Documents"
                           :value (->> (listen ::subs/items)
                                       (filter #(= (:status %) "completed"))
                                       (count))
                           :change -0.14}
                          {:name "Detected Frauds"
                           :value (->> (listen ::subs/items)
                                       (filter #(> (:score %) 0.8))
                                       (count))
                           :change 0.29}]]
          (doall
           (for [stat stat-items]
             ^{:key (:name stat)}
             [stat-item {:item stat}])))]

       ;; Bar Chart
       [:div
        [:div.flex.justify-between.mt-5
         [:div]
         [:div
          [button-group
           {:buttons
            [{:id :default
              :content "Default"
              :on-click #(reset! bar-vis :default)
              :active? (= :default @bar-vis)}
             {:id :document-type
              :content "Document Type"
              :on-click #(reset! bar-vis :document-type)
              :active? (= :document-type @bar-vis)}]}]]]
        [:div.h-64.w-full.mt-2
         [:> ResponsiveBar
          {:data (->> (listen ::subs/items)
                      (group-by #(format (js/Date. (:created %)) "yyyy-MM-dd"))
                      (map (fn [[date values]]
                             (assoc
                              (frequencies (map :type values))
                              :date date
                              :total_fraud (count (filter #(> (:score %) 0.8) values))
                              :total_not_fraud (count (filter #(<= (:score %) 0.8) values)))))
                      (sort-by :date))
           :margin {:bottom 30 :top 20 :left 60 :right 130}
           :indexBy "date"
           :keys (if (= @bar-vis :default)
                   [:total_not_fraud :total_fraud]
                   (distinct (map :type (listen ::subs/items))))
           :colors (if (= @bar-vis :default)
                     ["#86efac" "#22c55e"]
                     ["#ef4444" "#2563eb" "#14b8a6" "#9333ea" "#ec4899" "#06b6d4" "#f97316" "#f59e0b"])
           :enableGridY true
           :gridYValues 4
           :labelSkipWidth 10
           :labelSkipHeight 20
           :labelTextColor "white"
           :borderRadius 0
           :axisLeft {:tickSize 0
                      :tickValues 4
                      :legend "Uploaded Documents"
                      :legendPosition "middle"
                      :legendOffset -30}
           :axisBottom {:format #(format (js/Date. %) "MMM d")}
           :theme {:fontSize 13}
           :legends [{:dataFrom "keys"
                      :anchor "bottom-right"
                      :direction "column"
                      :justify false
                      :translateX 120
                      :itemsSpacing 2
                      :itemWidth 100
                      :itemHeight 20
                      :itemDirection "left-to-right"
                      :itemOpacity 0.85
                      :symbolSize 20
                      :effects [{:on "hover" :style {:itemOpacity 1}}]}]}]]]

       [:h3.mt-8.text-lg.leading-6.font-medium.text-gray-900
        "Recent Documents"]
       [:div
        [:div.mt-8.flex.flex-col
         [table
          {:headers [{:title "Record Reference"
                      :key :uuid
                      :render-cell (fn [uuid row]
                                     [:div.flex.items-center
                                      [:div.flex-shrink-0
                                       [:div.text-sm.leading-5.font-medium.text-gray-900.truncate.max-w-sm
                                        (:name row)]
                                       [:div.text-sm.leading-5.text-gray-500
                                        (str uuid)]]])}
                     {:title "Date"
                      :key :created
                      :render-cell (fn [date row]
                                     [:div.flex.items-center
                                      [:div.flex-shrink-0
                                       [:div.text-sm.leading-5.text-gray-900
                                        (format (js/Date. date) "PP")]
                                       [:div.text-sm.leading-5.text-gray-500
                                        (format (js/Date. date) "pp")]]])
                      :sorting {:comparator <}}
                     {:title "User"
                      :key :user
                      :sorting {:comparator <}}
                     {:title "Type"
                      :key :type
                      :sorting {:comparator <}
                      :render-cell (fn [type]
                                     [:span.inline-flex.items-center.px-2.5.py-0.5.rounded-full.text-xs.font-medium.uppercase.bg-gray-100.text-gray-800
                                      type])}
                     {:title "Fraud Score"
                      :key :score
                      :render-cell (fn [score]
                                     [:div.flex.items-center
                                      [:div.w-32.h-8
                                       [:> ResponsiveBullet
                                        {:data [{:id ""
                                                 :ranges [0 0.2 0.4 0.5 0.6 0.8 1]
                                                 :measures [score]
                                                 :markers [score]}]
                                         :rangeColors ["#a3e635" "#d9f99d" "#f7fee7" "#fef2f2" "#fecaca" "#f87171"]
                                         :markerColors ["#4b5563"]
                                         :measureSize 0
                                         :markerSize 1.2
                                         :margin {:top 5
                                                  :bottom 5
                                                  :left 0
                                                  :right 5}
                                         :theme {:axis {:ticks {:line {:strokeWidth 0}}}}}]]
                                      [:span.ml-2.w-4.text-center
                                       {:class (cond
                                                 (> score 0.8) "text-red-500 font-bold"
                                                 (> score 0.6) "text-red-400 font-bold"
                                                 (> score 0.4) "text-gray-900 font-semibold"
                                                 (< score 0.4) "text-green-500 font-semibold"
                                                 :else "text-gray-800 font-semibold")}
                                       (format-percent score :digits 0)]])
                      :sorting {:comparator <}}]
           :rows (listen ::subs/items)
           :actions [{:name :report
                      :icon DocumentSearchIcon
                      :on-click identity}
                     {:name :download
                      :icon DocumentDownloadIcon
                      :on-click identity}
                     {:name :edit
                      :icon ArchiveIcon
                      :on-click identity}]}]
         [pagination]]]])))

(defn content []
  [:div.bg-white.rounded-lg.shadow.px-5.py-6.sm:px-6
   [report-example]])
